<template>
  <div class="exploration-page">

    <!-- Floating back button -->
    <button class="back-btn" @click="goBack">
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
        <path d="M8 1L3 6l5 5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      Exploraciones
    </button>

    <!-- Page content -->
    <div class="ep-body">

      <!-- Browser frame -->
      <div class="browser-frame">
        <!-- Browser top bar -->
        <div class="bf-bar">
          <div class="bf-dots">
            <span class="bf-dot bf-dot--red"></span>
            <span class="bf-dot bf-dot--yellow"></span>
            <span class="bf-dot bf-dot--green"></span>
          </div>
          <div class="bf-address">
            <span class="bf-lock">
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                <rect x="1.5" y="4.5" width="7" height="4.5" rx="1" stroke="currentColor" stroke-width="1"/>
                <path d="M3 4.5V3a2 2 0 014 0v1.5" stroke="currentColor" stroke-width="1"/>
              </svg>
            </span>
            milo.design/exploraciones/{{ exploration ? exploration.slug : '' }}
          </div>
        </div>

        <!-- Frame content area -->
        <div class="bf-content" v-if="exploration">
          <!-- Live component preview -->
          <div class="ef-preview" :class="{ 'ef-preview--fullbleed': exploration.fullbleed }">
            <component :is="componentMap[exploration.component]" v-if="componentMap[exploration.component]" />
          </div>
        </div>

        <!-- Not found -->
        <div class="bf-content ef-notfound" v-else>
          <p>Exploración no encontrada.</p>
        </div>
      </div>

      <!-- Exploration details (below frame) -->
      <div class="ef-details" v-if="exploration">
        <div class="ef-details-left">
          <span class="ef-category">{{ exploration.category }}</span>
          <h1 class="ef-title">{{ exploration.title }}</h1>
        </div>
        <div class="ef-details-right">
          <span class="ef-tech-label">Stack</span>
          <span class="ef-tech">{{ exploration.tech }}</span>
        </div>
        <div class="ef-desc-block">
          <template v-if="exploration.sections">
            <p
              v-for="(s, i) in exploration.sections"
              :key="i"
              class="ef-desc"
            ><strong class="ef-section-label">{{ s.label }}:</strong> {{ s.text }}</p>
          </template>
          <template v-else>
            <p
              v-for="(para, i) in exploration.desc.split('\n\n')"
              :key="i"
              class="ef-desc"
            >{{ para }}</p>
          </template>
        </div>
      </div>

      <!-- Navegación anterior / siguiente -->
      <div class="ep-nav">
        <!-- Anterior -->
        <div class="ep-nav-side ep-nav-side--left">
          <span class="ep-nav-label">Anterior</span>
          <button class="ep-nav-btn ep-nav-btn--prev" @click="$router.push('/exploration/' + prevExploration.slug)">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M13 7H1M6 2L1 7l5 5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span class="ep-nav-title">{{ prevExploration.title }}</span>
          </button>
        </div>

        <!-- Separador central -->
        <div class="ep-nav-divider"></div>

        <!-- Siguiente -->
        <div class="ep-nav-side ep-nav-side--right">
          <span class="ep-nav-label">Siguiente</span>
          <button class="ep-nav-btn ep-nav-btn--next" @click="$router.push('/exploration/' + nextExploration.slug)">
            <span class="ep-nav-title">{{ nextExploration.title }}</span>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M1 7h12M8 2l5 5-5 5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import { explorations, getExploration } from '@/data/explorations.js';
import CardIkea from '@/components/CardIkea.vue';
import CardAvatar from '@/components/CardAvatar.vue';
import TweenNumber from '@/components/TweenNumber.vue';
import GlowButtonEffect from '@/components/GlowButtonEffect.vue';
import MusicPlayer from '@/components/MusicPlayer.vue';
import CardNotifications from '@/components/CardNotification.vue';
import VisibilitySettings from '@/components/VisibilitySettings.vue';
import CardSocial from '@/components/CardSocial.vue';
import UploadProgress from '@/components/UploadProgress.vue';
import OnboardingCard from '@/components/OnboardingCard.vue';
import HamburguerMenu from '@/components/HamburguerMenu.vue';
import OryzoHeader from '@/components/OryzoHeader.vue';
import RaycastFeatures from '@/components/RaycastFeatures.vue';
import FileUploadCard from '@/components/FileUploadCard.vue';
import AuthKitHero from '@/components/AuthKitHero.vue';
import AgentCard from '@/components/AgentCard.vue';
import AppleScrollEffect from '@/components/AppleScrollEffect.vue';

export default {
  name: 'ExplorationPage',

  components: {
    CardIkea, CardAvatar, TweenNumber, GlowButtonEffect, MusicPlayer,
    CardNotifications, VisibilitySettings, CardSocial, UploadProgress,
    OnboardingCard, HamburguerMenu, OryzoHeader, RaycastFeatures, FileUploadCard, AuthKitHero, AgentCard,
    AppleScrollEffect,
  },

  data() {
    return {
      componentMap: {
        CardIkea, CardAvatar, TweenNumber, GlowButtonEffect, MusicPlayer,
        CardNotifications, VisibilitySettings, CardSocial, UploadProgress,
        OnboardingCard, HamburguerMenu, OryzoHeader, RaycastFeatures, FileUploadCard, AuthKitHero, AgentCard,
        AppleScrollEffect,
      }
    };
  },

  computed: {
    exploration() {
      return getExploration(this.$route.params.slug);
    },
    nextExploration() {
      const idx = explorations.findIndex(e => e.slug === this.$route.params.slug);
      return idx >= 0 && idx < explorations.length - 1 ? explorations[idx + 1] : explorations[0];
    },
    prevExploration() {
      const idx = explorations.findIndex(e => e.slug === this.$route.params.slug);
      return idx > 0 ? explorations[idx - 1] : explorations[explorations.length - 1];
    }
  },

  methods: {
    goBack() {
      if (window.history.length > 1) {
        this.$router.go(-1);
      } else {
        this.$router.push('/');
      }
    }
  },

  mounted() {
    document.body.style.backgroundColor = '#F7F6F2';
    document.body.style.padding = '0';
    window.scrollTo(0, 0);
  },

  beforeUnmount() {
    document.body.style.backgroundColor = '';
    document.body.style.padding = '';
  }
}
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Geist:wght@300;400;500;600;700&family=Geist+Mono:wght@400;500&display=swap');
</style>

<style scoped>
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

.exploration-page {
  min-height: 100vh;
  background: var(--bg, #F7F6F2);
  font-family: 'Geist', 'Inter', ui-sans-serif, system-ui, sans-serif;
  -webkit-font-smoothing: antialiased;
}

/* ── FLOATING BACK BUTTON ── */
.back-btn {
  position: fixed;
  left: 40px;
  top: 48px;
  z-index: 50;
  display: flex;
  align-items: center;
  gap: 7px;
  font-family: inherit;
  font-size: 12.5px;
  font-weight: 500;
  color: var(--muted, #6B6B63);
  background: rgba(247, 246, 242, 0.75);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: none;
  border-radius: 8px;
  cursor: pointer;
  padding: 6px 10px;
  letter-spacing: -0.1px;
  transition: color .15s, transform .15s;
}
.back-btn:hover {
  color: var(--ink, #13130F);
  transform: translateX(-2px);
}
.back-btn svg { flex-shrink: 0; }

/* ── PAGE BODY ── */
.ep-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 120px 80px;
  gap: 32px;
  min-height: 100vh;
}

/* ── BROWSER FRAME ── */
.browser-frame {
  width: 100%;
  max-width: 860px;
  border-radius: 14px;
  border: 1px solid rgba(19,19,15,0.1);
  overflow: hidden;
  box-shadow:
    0 0 0 1px rgba(19,19,15,0.04),
    0 4px 6px -2px rgba(19,19,15,0.04),
    0 24px 48px -12px rgba(19,19,15,0.08);
  background: #fff;
}

/* Top bar */
.bf-bar {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 11px 16px;
  background: #F0EFEA;
  border-bottom: 1px solid rgba(19,19,15,0.08);
}
.bf-dots { display: flex; gap: 6px; align-items: center; }
.bf-dot { width: 11px; height: 11px; border-radius: 50%; }
.bf-dot--red    { background: #FF5F57; }
.bf-dot--yellow { background: #FFBD2E; }
.bf-dot--green  { background: #28C840; }

.bf-address {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-family: 'Geist Mono', monospace;
  font-size: 11px;
  color: var(--muted, #6B6B63);
  background: rgba(255,255,255,.7);
  padding: 4px 14px;
  border-radius: 6px;
  max-width: 360px;
  margin: 0 auto;
  letter-spacing: -0.1px;
}
.bf-lock { color: var(--muted-2, #9A9A90); display: flex; align-items: center; }

/* Frame content */
.bf-content { background: #fff; }

/* ── LIVE COMPONENT PREVIEW ── */
.ef-preview {
  min-height: 420px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 32px;
  background: #fff;
  overflow-y: auto;
}

.ef-preview--fullbleed {
  padding: 0;
  min-height: unset;
  height: 560px;
  display: block;
  overflow: hidden;
}

/* Not found */
.ef-notfound {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 80px;
  color: var(--muted, #6B6B63);
  font-size: 14px;
}

/* ── DETAILS ROW ── */
.ef-details {
  width: 100%;
  max-width: 860px;
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-rows: auto auto;
  column-gap: 32px;
  row-gap: 16px;
  padding: 4px 0;
}
.ef-details-left {
  grid-column: 1;
  grid-row: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.ef-details-right {
  grid-column: 2;
  grid-row: 1;
  display: flex;
  flex-direction: column;
  gap: 5px;
  align-items: flex-end;
}
.ef-desc-block {
  grid-column: 1 / 3;
  grid-row: 2;
  display: flex;
  flex-direction: column;
}
.ef-category {
  font-family: 'Geist Mono', monospace;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: .1em;
  text-transform: uppercase;
  color: var(--muted-2, #9A9A90);
}
.ef-title {
  font-size: 20px;
  font-weight: 500;
  letter-spacing: -0.03em;
  color: var(--ink, #13130F);
  line-height: 1.2;
}
.ef-desc {
  font-size: 14px;
  line-height: 1.65;
  color: var(--muted, #6B6B63);
}
.ef-desc + .ef-desc {
  margin-top: 16px;
}
.ef-section-label {
  font-weight: 600;
  color: var(--ink, #13130F);
}
.ef-tech-label {
  font-family: 'Geist Mono', monospace;
  font-size: 9.5px;
  font-weight: 500;
  letter-spacing: .1em;
  text-transform: uppercase;
  color: var(--muted-2, #9A9A90);
}
.ef-tech {
  font-family: 'Geist Mono', monospace;
  font-size: 12px;
  font-weight: 500;
  color: var(--ink, #13130F);
  padding: 4px 10px;
  border-radius: 6px;
  background: rgba(19,19,15,0.04);
  border: 1px solid rgba(19,19,15,0.08);
}

/* ── NAVEGACIÓN ANTERIOR / SIGUIENTE ── */
.ep-nav {
  width: 100%;
  max-width: 860px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 24px 0;
  border-top: 1px solid rgba(19,19,15,0.08);
}

.ep-nav-divider {
  width: 1px;
  height: 32px;
  background: rgba(19,19,15,0.08);
}

.ep-nav-side {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.ep-nav-side--right { align-items: flex-end; }

.ep-nav-label {
  font-family: 'Geist Mono', monospace;
  font-size: 9.5px;
  font-weight: 500;
  letter-spacing: .1em;
  text-transform: uppercase;
  color: var(--muted-2, #9A9A90);
}

.ep-nav-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  width: fit-content;
  background: #fff;
  border: 1px solid rgba(19,19,15,0.1);
  cursor: pointer;
  font-family: inherit;
  padding: 8px 14px;
  border-radius: 8px;
  transition: background .15s, border-color .15s, transform .15s;
}
.ep-nav-btn:hover {
  background: var(--ink, #13130F);
  border-color: var(--ink, #13130F);
}
.ep-nav-title {
  font-size: 13px;
  font-weight: 500;
  color: var(--ink, #13130F);
  letter-spacing: -0.01em;
  transition: color .15s;
}
.ep-nav-btn:hover .ep-nav-title { color: rgba(255,255,255,.85); }
.ep-nav-btn svg { flex-shrink: 0; transition: transform .15s; }
.ep-nav-btn svg path { transition: stroke .15s; }
.ep-nav-btn:hover svg path { stroke: rgba(255,255,255,.85); }

.ep-nav-btn--prev:hover { transform: translateX(-2px); }
.ep-nav-btn--next:hover { transform: translateX(2px); }
.ep-nav-btn--prev:hover svg { transform: translateX(-2px); }
.ep-nav-btn--next:hover svg { transform: translateX(2px); }

/* ── RESPONSIVE ── */
@media (max-width: 900px) {
  .back-btn {
    position: fixed;
    left: 12px;
    top: 16px;
    transform: none;
  }
  .back-btn:hover { transform: translateX(-2px); }
  .ep-body { padding: 64px 16px 60px; }
  .ef-details { grid-template-columns: 1fr; grid-template-rows: auto auto auto; }
  .ef-details-right { grid-column: 1; grid-row: 2; align-items: flex-start; }
  .ef-desc-block { grid-row: 3; }
  .ef-preview { padding: 24px 16px; min-height: 300px; }

  .ep-nav { gap: 16px; }
  .ep-nav-btn { padding: 7px 11px; }
  .ep-nav-title { font-size: 12px; }
}
</style>
